import { Link } from "react-router-dom";
import { Play, X, Clock } from "lucide-react";
import { posterUrl } from "../lib/api";

function formatRemaining(positionSec, durationSec) {
  const remaining = Math.max(0, (durationSec || 0) - (positionSec || 0));
  if (!remaining) return "Resume";
  if (remaining < 60) return `${Math.round(remaining)}s left`;
  const mins = Math.round(remaining / 60);
  return mins >= 60 ? `${Math.round(mins / 60)}h left` : `${mins} min left`;
}

export default function ContinueWatchingRail({ items, onDismiss }) {
  if (!items?.length) return null;
  return (
    <section className="mb-12" data-testid="continue-watching-rail">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-2xl sm:text-3xl font-bold">
          Continue <span className="text-[#E65F2B]">watching</span>
        </h2>
        <span className="text-xs text-white/40">{items.length} title{items.length === 1 ? "" : "s"}</span>
      </div>
      <div className="flex gap-4 overflow-x-auto row-scroll pb-4">
        {items.map((m, i) => {
          const pct = Math.max(2, Math.min(98, Math.round((m.progress?.pct || 0) * 100)));
          return (
            <div
              key={m.id}
              data-testid={`cw-item-${m.id}`}
              className="w-64 sm:w-80 shrink-0 group relative animate-fade-up"
              style={{ animationDelay: `${i * 60}ms`, opacity: 0 }}
            >
              <Link to={`/watch/${m.id}`} className="block">
                <div className="relative aspect-video overflow-hidden rounded-lg border border-white/5 bg-[#141414]">
                  <img
                    src={posterUrl(m.id)}
                    alt={m.title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="absolute inset-0 poster-overlay" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="h-14 w-14 rounded-full bg-[#E65F2B] flex items-center justify-center shadow-2xl">
                      <Play className="h-6 w-6 text-white fill-white ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0">
                    <div className="px-3 pb-2">
                      <p className="text-[10px] uppercase tracking-wider text-[#E65F2B] font-semibold">{m.genre}</p>
                      <h3 className="font-display font-bold text-white leading-tight line-clamp-1">{m.title}</h3>
                      <p className="text-[11px] text-white/60 mt-0.5 flex items-center gap-1.5">
                        <Clock className="h-3 w-3" /> {formatRemaining(m.progress?.position_seconds, m.progress?.duration_seconds)}
                      </p>
                    </div>
                    {/* Progress bar */}
                    <div className="h-1 w-full bg-white/10">
                      <div className="h-full bg-[#E65F2B]" style={{ width: `${pct}%` }} data-testid={`cw-progress-${m.id}`} />
                    </div>
                  </div>
                </div>
              </Link>
              <button
                onClick={(e) => { e.preventDefault(); onDismiss?.(m.id); }}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/70 backdrop-blur-sm border border-white/10 text-white/70 hover:text-white hover:bg-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove from Continue Watching"
                data-testid={`cw-dismiss-${m.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
